/**
 * Trial-balance endpoint (bos-api).
 *
 * The path is relative to the axios client's base, which already ends in
 * `/api/v1` — so `/ledger/trial-balance` reaches
 * `…/api/v1/ledger/trial-balance`. This mirrors `AccountsEndpoints`,
 * `JournalEndpoints`, `StatementsEndpoints` and `VatEndpoints`, the other live
 * ledger resources.
 *
 * Note it is **not** under `/ledger/statements/`. The trial balance is a working
 * document rather than a published statement, and the API puts it at the ledger
 * root accordingly.
 *
 * Three parameters, all measured against the live server on 2026-08-02:
 *
 * - **`asOf`** — inclusive, `YYYY-MM-DD`. Omitting it is "all history", not
 *   "today"; every read here sends one so the report always states a date.
 * - **`locale`** — account names are resolved **server-side**, so the locale is
 *   part of the request and switching the app language refetches rather than
 *   re-resolving a map the client holds. Verified: `locale=ka` returns
 *   `სალარო` where `locale=en` returns `Cash on hand`.
 * - **`excludeClosing`** — omits entries whose source is `CLOSING`, giving the
 *   year's trading position as it stood before the books were closed. Confirmed
 *   wired (it is not one of the parameters this API silently ignores); see
 *   `CONTRACT.md` for what the measurement could and could not show on the dev
 *   tenant.
 *
 * There is deliberately **no zero-row parameter to send**, because the API has
 * none: rows with a nil balance are dropped server-side and cannot be asked for.
 * Recorded here so the absence reads as measured rather than forgotten.
 */

const search = (entries: Record<string, string>): string => {
    const query = new URLSearchParams(entries).toString();
    return query ? `?${query}` : '';
};

export type TrialBalanceQuery = {
    /** ISO `YYYY-MM-DD`, inclusive. */
    asOf: string;
    /** Which locale the server should resolve every account `name` into. */
    locale: string;
    /** Leave year-end `CLOSING` entries out of the figures. */
    excludeClosing: boolean;
};

export const TrialBalanceEndpoints = {
    /** GET — every account with a balance as of a date, split debit / credit. */
    trialBalance: ({ asOf, locale, excludeClosing }: TrialBalanceQuery) =>
        `/ledger/trial-balance${search({
            asOf,
            locale,
            excludeClosing: String(excludeClosing),
        })}`,
};
